let lockCount = 0;
let scrollY = 0;

let previousBodyOverflow: string | null = null;
let previousBodyPosition: string | null = null;
let previousBodyTop: string | null = null;
let previousBodyWidth: string | null = null;
let previousHtmlOverflow: string | null = null;
let previousHtmlOverscrollBehavior: string | null = null;
let previousBodyOverscrollBehavior: string | null = null;

function clearLockedStyles() {
  if (typeof document === "undefined") return;

  document.documentElement.style.overflow = "";
  document.documentElement.style.overscrollBehavior = "";
  document.body.style.overflow = "";
  document.body.style.overscrollBehavior = "";
  document.body.style.position = "";
  document.body.style.top = "";
  document.body.style.width = "";
  document.body.style.pointerEvents = "";

  document.body.removeAttribute("data-scroll-locked");
}

function isElementVisiblyBlockingScroll(element: Element): boolean {
  if (!(element instanceof HTMLElement)) return false;

  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden" || style.pointerEvents === "none") {
    return false;
  }

  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/**
 * Locks document body scrolling with ref-counting so concurrent overlays
 * cannot accidentally re-enable scroll too early.
 *
 * Mobile-safe strategy:
 * - lock both html + body overflow
 * - freeze body with position: fixed
 * - preserve and restore previous scrollY
 */
export function lockBodyScroll(): () => void {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return () => undefined;
  }

  if (lockCount === 0) {
    scrollY = window.scrollY;

    previousBodyOverflow = document.body.style.overflow;
    previousBodyPosition = document.body.style.position;
    previousBodyTop = document.body.style.top;
    previousBodyWidth = document.body.style.width;
    previousHtmlOverflow = document.documentElement.style.overflow;
    previousHtmlOverscrollBehavior = document.documentElement.style.overscrollBehavior;
    previousBodyOverscrollBehavior = document.body.style.overscrollBehavior;

    document.documentElement.style.overflow = "hidden";
    document.documentElement.style.overscrollBehavior = "none";
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
  }

  lockCount += 1;

  return () => {
    if (typeof document === "undefined" || typeof window === "undefined" || lockCount === 0) return;

    lockCount -= 1;

    if (lockCount === 0) {
      document.documentElement.style.overflow = previousHtmlOverflow ?? "";
      document.documentElement.style.overscrollBehavior = previousHtmlOverscrollBehavior ?? "";
      document.body.style.overflow = previousBodyOverflow ?? "";
      document.body.style.overscrollBehavior = previousBodyOverscrollBehavior ?? "";
      document.body.style.position = previousBodyPosition ?? "";
      document.body.style.top = previousBodyTop ?? "";
      document.body.style.width = previousBodyWidth ?? "";

      window.scrollTo(0, scrollY);

      previousHtmlOverflow = null;
      previousHtmlOverscrollBehavior = null;
      previousBodyOverscrollBehavior = null;
      previousBodyOverflow = null;
      previousBodyPosition = null;
      previousBodyTop = null;
      previousBodyWidth = null;
    }
  };
}

export function __resetBodyScrollLockForTests() {
  lockCount = 0;
  scrollY = 0;
  previousHtmlOverflow = null;
  previousHtmlOverscrollBehavior = null;
  previousBodyOverscrollBehavior = null;
  previousBodyOverflow = null;
  previousBodyPosition = null;
  previousBodyTop = null;
  previousBodyWidth = null;
}

/**
 * iOS/Safari and some dialog stacks can occasionally leave stale styles on body
 * after abrupt route transitions. This fallback only runs when there is no active
 * lock in this module and no visible modal overlay.
 */
export function recoverBodyScrollIfStuck() {
  if (typeof document === "undefined") return;

  const possibleBlockingLayers = document.querySelectorAll(
    [
      "[aria-modal='true'][data-state='open']",
      "[role='dialog'][data-state='open']",
      "[role='alertdialog'][data-state='open']",
      "[data-state='open'][data-vaul-drawer]",
    ].join(","),
  );

  const hasVisibleOverlay = Array.from(possibleBlockingLayers).some(isElementVisiblyBlockingScroll);

  if (hasVisibleOverlay) return;

  const bodyLooksLocked =
    document.body.style.position === "fixed" ||
    document.body.style.overflow === "hidden" ||
    document.documentElement.style.overflow === "hidden" ||
    document.body.style.pointerEvents === "none" ||
    document.body.hasAttribute("data-scroll-locked");

  if (!bodyLooksLocked) return;

  clearLockedStyles();
  lockCount = 0;
  scrollY = 0;
  previousHtmlOverflow = null;
  previousHtmlOverscrollBehavior = null;
  previousBodyOverscrollBehavior = null;
  previousBodyOverflow = null;
  previousBodyPosition = null;
  previousBodyTop = null;
  previousBodyWidth = null;
}
